import type { AnalyticsState } from 'store'
import type { AnalyticEvents } from '@/models/Analytics.model'
import type {
  AnalyticsCreateEcommerceParams,
  AnalyticsCreateEventParams,
  CompilationItem,
  Ticket,
  createAnalyticsEcommerceEventSliderParams,
  EcommerceAnalyticItem,
  DateRange,
  Venue,
  Category,
  CompilationActivity,
  CompilationEvent,
} from '@/models'

export const useAnalyticsStore = defineStore('analytics.store', () => {
  const nuxtApp = useNuxtApp()

  const sendedCompilationsKassir = ref<number[]>([])
  const sendedRecommendationsRetailRocket = ref<number[]>([])
  const sendedBanners = ref<number[]>([])

  const isEventSended = (valueName: keyof AnalyticsState, id: number) => {
    switch (valueName) {
      case 'sendedCompilationsKassir':
        return sendedCompilationsKassir.value.includes(id)
      case 'sendedRecommendationsRetailRocket':
        return sendedRecommendationsRetailRocket.value.includes(id)
      case 'sendedBanners':
        return sendedBanners.value.includes(id)
    }
  }
  /**
   * создает обычное событие в аналитику
   * @param createObj
   */
  // Почему называется createAnalyticsEvent когда работает только с Яндеском?
  const createAnalyticsEvent = (createObj: AnalyticsCreateEventParams) => {
    // события gtm  (не ecommerce)
    nuxtApp.$analytics.yandexMetrika.create(createObj)
  }
  /**
   * создает ecommerce событие в аналитику
   * @param createObj
   */
  const createAnalyticsEcommerceEvent = (
    createObj: AnalyticsCreateEcommerceParams,
  ) => {
    nuxtApp.$analytics.ecommerce.create(createObj)
  }

  const getCompilationItemEcommerce = (
    item: CompilationItem,
  ): EcommerceAnalyticItem => {
    if (item.type === 'venue') return {}

    const formatAnalyticsDate = (date?: string): string | undefined => {
      return date && formatDate(date, 'dd.MM.yyyy')
    }

    const isActivity = (item: CompilationItem) => item.type === 'activity'

    const { object: itemObject } = item

    const {
      id,
      priceRange: { min: minPrice, max: maxPrice },
      title,
    } = itemObject as CompilationActivity | CompilationEvent

    const params: EcommerceAnalyticItem = {
      item_id: id,
      min_price: minPrice,
      max_price: maxPrice,
      item_name: title,
      quantity: 1,
    }

    const setCommonParams = (
      dateRange?: DateRange,
      venue?: Venue,
      category?: Category,
    ): void => {
      params.date_from = formatAnalyticsDate(dateRange?.beginsAt)
      params.date_to = formatAnalyticsDate(dateRange?.endsAt)
      params.item_brand = venue?.name
      params.item_category = category?.name
      params.item_category2 = category?.parent?.name
    }

    if (isActivity(item)) {
      const { dateRange, venues, category } = itemObject as CompilationActivity

      setCommonParams(dateRange, venues[0], category)
    } else {
      const { beginsAt, endsAt, venue, activity } =
        itemObject as CompilationEvent

      setCommonParams({ beginsAt, endsAt }, venue, activity?.category)
    }

    return params
  }
  /**
   * добавляет слайд в отправленные
   * @param analyticsName - имя массива, в который сохраняем
   * @param value - id, в которое сохранаяем
   */
  const addAnalyticsSlideToSended = (
    analyticsName: keyof AnalyticsState,
    value: number,
  ) => {
    switch (analyticsName) {
      case 'sendedCompilationsKassir':
        return sendedCompilationsKassir.value.push(value)
      case 'sendedRecommendationsRetailRocket':
        return sendedRecommendationsRetailRocket.value.push(value)
      case 'sendedBanners':
        return sendedBanners.value.push(value)
    }
  }
  /**
   * создает ecommerce событые в аналитику для слайдеров.
   * используется, когда нужно отловить
   * событие показа новго слайда,
   * при это не триггерясь на уже просмотренные
   * @param obj
   */
  const createAnalyticsEcommerceEventSlider = (
    obj: createAnalyticsEcommerceEventSliderParams,
  ) => {
    const { createObj, analyticsName, itemId } = obj

    createAnalyticsEcommerceEvent({
      event: createObj.event,
      value: [...createObj.value],
    })
    // запоминаем, что слайд уже показан
    addAnalyticsSlideToSended(analyticsName, itemId)
  }
  /**
   * обработчик смены слайда
   * @param items - список слайдов
   * @param slidesLength - кол-во показанных слайдов
   * @param locationId - название места на сайте из ТЗ аналитики
   * @param analyticsName - имя перемнной,
   * в которой хранятся уже показанные слайды
   * @param event
   */
  const slidesChangeAnalyticsHandler = (
    items: any[],
    slidesLength: number,
    locationId: string,
    analyticsName: keyof AnalyticsState,
    event: AnalyticEvents,
  ) => {
    // проходим от первого до последнего показанного слайда
    for (let i = 0; i < slidesLength; i++) {
      const item = items[i]?.object || items[i]
      const itemIsSended = isEventSended(analyticsName, item?.id)

      // если слайд уже показан, не создаем событие
      if (!itemIsSended && item) {
        createAnalyticsEcommerceEventSlider({
          createObj: {
            event,
            value: [
              {
                promotion_name: item.posterImage,
                url: item.urlSlug || item.href,
                promotion_id: item.id,
                index: i,
                location_id: locationId,
                count_banner: items?.length,
              },
            ],
          },
          analyticsName,
          itemId: item.id,
        })
      }
    }
  }

  const getEcommerceTickets = (tickets: Ticket[]) => {
    return tickets.map((ticket) => {
      return {
        ...ticket,
        discount: ticket.discount || 0,
        quantity: 1,
      }
    })
  }

  return {
    sendedCompilationsKassir,
    sendedRecommendationsRetailRocket,
    sendedBanners,
    isEventSended,
    createAnalyticsEvent,
    createAnalyticsEcommerceEvent,
    addAnalyticsSlideToSended,
    createAnalyticsEcommerceEventSlider,
    slidesChangeAnalyticsHandler,
    getEcommerceTickets,
    getCompilationItemEcommerce,
  }
})
